import React, { useEffect, useState } from "react";
import { api, type SessionUser } from "../api/client";
import { CardFaceRenderer, faceHasContent, type FaceFields } from "./CardFaceRenderer";
import { getOverlayCardEntry } from "../lib/overlayClient";
import { RenderPreview } from "./RenderPreview";
import { NotesPanel } from "./NotesPanel";
import { EditHistory } from "./EditHistory";
import { ReviewStatusSelect } from "./ReviewStatusBadge";

interface TranslationRow {
  id: string;
  lang_code: string;
  status: string;
}

interface CardData {
  id: string;
  name?: string;
  front?: FaceFields;
  back?: FaceFields;
}

/** Fusionne le texte original et l'overlay de traduction champ par champ :
 * un champ non encore traduit retombe sur le texte original (comportement
 * Shoggoth natif : l'overlay ne modifie que ce qu'il contient). */
function mergeFace(original: FaceFields | undefined, overlay: Partial<FaceFields> | undefined): FaceFields {
  if (!original) return overlay ?? {};
  if (!overlay) return original;
  return { ...original, ...overlay };
}

/**
 * Vue unique pour lire, relire et traduire une carte.
 *
 * Important pour la performance : le texte ORIGINAL vient de `cards` (déjà
 * chargé en une fois par ProjectView, aucun appel réseau ici), et l'overlay
 * de traduction complet vient de `getTranslationOverlay` — mis en cache par
 * langue dans ProjectView après un premier fetch dont le JSON.parse a lieu
 * dans le navigateur (jamais sur le Worker : un vrai fichier de traduction
 * Shoggoth peut peser plusieurs centaines de Ko à plusieurs Mo, ce qui
 * dépassait le budget CPU de 10 ms par requête du Worker et gelait
 * l'interface). Changer de carte, une fois la langue déjà chargée, ne fait
 * plus AUCUN appel réseau — juste une lecture dans l'objet déjà en mémoire.
 */
export function CardEditor({
  projectId,
  cardId,
  cards,
  getTranslationOverlay,
  invalidateTranslationOverlay,
  user,
  canWrite,
  onOriginalStatusChange,
}: {
  projectId: string;
  cardId: string;
  cards: CardData[];
  getTranslationOverlay: (lang: string) => Promise<any>;
  invalidateTranslationOverlay: (lang: string) => void;
  user: SessionUser | null;
  canWrite: boolean;
  onOriginalStatusChange?: (status: "unreviewed" | "reviewed" | "needs_correction") => void;
}) {
  const [translations, setTranslations] = useState<TranslationRow[]>([]);
  const [lang, setLang] = useState<string | null>(null); // null = original
  const [overlayEntry, setOverlayEntry] = useState<{ name?: string; front?: Partial<FaceFields>; back?: Partial<FaceFields> } | null>(
    null
  );
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [overlayError, setOverlayError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [addingLang, setAddingLang] = useState(false);
  const [newLangCode, setNewLangCode] = useState("");
  const [checkingRepo, setCheckingRepo] = useState(false);

  const card = cards.find((c) => c.id === cardId);

  async function reloadTranslations() {
    const r = await api.listTranslations(projectId);
    setTranslations(r.translations);
    return r.translations as TranslationRow[];
  }

  useEffect(() => {
    reloadTranslations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setCompareOpen(false);
    if (!lang) {
      setOverlayEntry(null);
      return;
    }
    let cancelled = false;
    setLoadingOverlay(true);
    setOverlayError(null);
    getTranslationOverlay(lang)
      .then((overlayDoc) => {
        if (cancelled) return;
        setOverlayEntry(getOverlayCardEntry(overlayDoc, cardId) ?? null);
      })
      .catch((e: any) => {
        if (!cancelled) setOverlayError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingOverlay(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, lang]);

  async function handleCheckRepoTranslations() {
    setCheckingRepo(true);
    try {
      await api.syncProject(projectId);
      await reloadTranslations();
      setSaveError(null);
    } catch (e: any) {
      setSaveError(e.message);
    } finally {
      setCheckingRepo(false);
    }
  }

  async function handleCreateLanguage(e: React.FormEvent) {
    e.preventDefault();
    if (!newLangCode.trim()) return;
    try {
      const { langCode } = await api.addTranslation(projectId, newLangCode.trim());
      invalidateTranslationOverlay(langCode);
      await reloadTranslations();
      setLang(langCode);
      setNewLangCode("");
      setAddingLang(false);
      setSaveError(null);
    } catch (e: any) {
      setSaveError(e.message);
    }
  }

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    let content: unknown;
    try {
      content = JSON.parse(await file.text());
    } catch {
      setSaveError(`« ${file.name} » n'est pas un fichier JSON valide.`);
      return;
    }

    const guessedLang =
      (typeof (content as any)?.language === "string" && (content as any).language) ||
      (typeof (content as any)?.lang === "string" && (content as any).lang) ||
      file.name.replace(/\.json$/i, "");

    const langCode = window.prompt(`Code langue pour ce fichier (détecté : « ${guessedLang} »)`, guessedLang);
    if (!langCode?.trim()) return;

    try {
      const result = await api.importTranslation(projectId, langCode.trim(), content);
      invalidateTranslationOverlay(result.langCode);
      await reloadTranslations();
      setLang(result.langCode);
      setSaveError(null);
    } catch (e: any) {
      setSaveError(e.message);
    }
  }

  async function handleFieldChange(face: "front" | "back", field: keyof FaceFields, value: string) {
    if (!lang) {
      // L'édition du texte ORIGINAL n'est pas mise en cache ici (elle vient
      // de `cards`, propriété du parent) : ProjectView recharge la liste des
      // cartes après un export, ce qui suffit pour ce cas d'usage.
      try {
        await api.setOriginalField(projectId, cardId, face, String(field), value);
        setSaveError(null);
      } catch (e: any) {
        setSaveError(e.message);
      }
      return;
    }

    // Mise à jour optimiste locale avant la réponse réseau.
    setOverlayEntry((prev) => ({ ...(prev ?? {}), [face]: { ...((prev as any)?.[face] ?? {}), [field]: value } }));
    try {
      await api.setTranslatedField(projectId, lang, cardId, face, String(field), value);
      invalidateTranslationOverlay(lang); // le cache mémoire ne reflète plus le fichier stocké
      setSaveError(null);
    } catch (e: any) {
      setSaveError(e.message);
    }
  }

  async function handleNameChange(value: string) {
    if (!lang) return; // l'édition du nom ORIGINAL n'est pas encore supportée côté API
    setOverlayEntry((prev) => ({ ...(prev ?? {}), name: value }));
    try {
      await api.setTranslatedName(projectId, lang, cardId, value);
      invalidateTranslationOverlay(lang);
      setSaveError(null);
    } catch (e: any) {
      setSaveError(e.message);
    }
  }

  async function handleStatusChange(newStatus: string) {
    if (!lang) return;
    await api.setTranslationStatus(projectId, lang, newStatus as any);
    await reloadTranslations();
  }

  if (!card) return <p>Chargement de la carte…</p>;
  if (lang && loadingOverlay && !overlayEntry) return <p>Chargement de la traduction…</p>;

  const frontFace = mergeFace(card.front, lang ? overlayEntry?.front : undefined);
  const backFace = mergeFace(card.back, lang ? overlayEntry?.back : undefined);
  const displayName = lang ? overlayEntry?.name ?? card.name : card.name;
  const backHasContent = faceHasContent(backFace);
  // Une seule illustration recto/verso (cas fréquent des lieux) : ne pas la
  // montrer deux fois d'affilée.
  const backImageIsDuplicate = !!frontFace.illustration && frontFace.illustration === backFace.illustration;

  const currentTranslationRow = translations.find((t) => t.lang_code === lang);

  return (
    <div className="card-editor">
      <div className="card-editor__toolbar">
        <label className="card-editor__lang-select">
          Langue :
          <select value={lang ?? ""} onChange={(e) => setLang(e.target.value || null)}>
            <option value="">Original</option>
            {translations.map((t) => (
              <option key={t.id} value={t.lang_code}>
                {t.lang_code} {t.status !== "published" ? `(${t.status === "draft" ? "brouillon" : "en revue"})` : ""}
              </option>
            ))}
          </select>
        </label>

        <ReviewStatusSelect
          projectId={projectId}
          cardId={cardId}
          lang={lang}
          editable={canWrite}
          onChanged={(status) => {
            if (!lang) onOriginalStatusChange?.(status);
          }}
        />

        {lang && canWrite && (
          <label className="card-editor__translation-status">
            Statut de la traduction :
            <select value={currentTranslationRow?.status ?? "draft"} onChange={(e) => handleStatusChange(e.target.value)}>
              <option value="draft">Brouillon</option>
              <option value="in_review">En revue</option>
              <option value="published">Publiée</option>
            </select>
          </label>
        )}

        {!lang && translations.length > 0 && (
          <button className="btn btn--small btn--ghost" onClick={() => setCompareOpen((o) => !o)}>
            {compareOpen ? "Masquer" : "Comparer"} une traduction
          </button>
        )}

        {canWrite &&
          (addingLang ? (
            <form className="card-editor__add-lang" onSubmit={handleCreateLanguage}>
              <input
                className="input"
                placeholder="Code langue (ex: fr, es, de)"
                value={newLangCode}
                onChange={(e) => setNewLangCode(e.target.value)}
                autoFocus
              />
              <button className="btn btn--small" type="submit">
                Créer
              </button>
              <button className="btn btn--ghost btn--small" type="button" onClick={() => setAddingLang(false)}>
                Annuler
              </button>
            </form>
          ) : (
            <div className="card-editor__lang-actions">
              <button className="btn btn--small btn--ghost" onClick={() => setAddingLang(true)}>
                + Langue
              </button>
              <label className="btn btn--small btn--ghost btn--file">
                Importer…
                <input type="file" accept="application/json,.json" onChange={handleImportFile} hidden />
              </label>
              <button className="btn btn--small btn--ghost" onClick={handleCheckRepoTranslations} disabled={checkingRepo}>
                {checkingRepo ? "Vérification…" : "Vérifier le dépôt"}
              </button>
            </div>
          ))}
      </div>

      {saveError && <p className="error">{saveError}</p>}
      {overlayError && <p className="error">Traduction indisponible : {overlayError}</p>}

      <div className="card-editor__body">
        <div className="card-editor__main">
          {lang && canWrite ? (
            <input
              className="card-editor__name-input"
              value={displayName ?? ""}
              placeholder={card.name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
          ) : (
            <h2 className="card-editor__name">{displayName}</h2>
          )}

          <div className="card-editor__faces">
            <CardFaceRenderer
              face={frontFace}
              cardName={displayName}
              editable={canWrite}
              onFieldChange={(field, value) => handleFieldChange("front", field, value)}
              faceLabel={backHasContent || canWrite ? "Recto" : undefined}
            />
            {(backHasContent || canWrite) && (
              <CardFaceRenderer
                face={backFace}
                cardName={displayName}
                editable={canWrite}
                onFieldChange={(field, value) => handleFieldChange("back", field, value)}
                faceLabel="Verso"
                showIllustration={!backImageIsDuplicate}
              />
            )}
          </div>

          {compareOpen && !lang && (
            <CompareReference
              card={card}
              translations={translations}
              getTranslationOverlay={getTranslationOverlay}
            />
          )}

          <RenderPreview projectId={projectId} cardId={cardId} lang={lang} />
          <EditHistory projectId={projectId} cardId={cardId} lang={lang} />
        </div>

        <NotesPanel projectId={projectId} cardId={cardId} lang={lang} user={user} />
      </div>
    </div>
  );
}

/** Panneau de comparaison optionnel : affiche une traduction en lecture
 * seule à côté du texte original, pour un relecteur qui veut vérifier une
 * traduction sans basculer la carte entière sur cette langue. Utilise le
 * même cache navigateur (aucun appel réseau si la langue est déjà chargée). */
function CompareReference({
  card,
  translations,
  getTranslationOverlay,
}: {
  card: CardData;
  translations: TranslationRow[];
  getTranslationOverlay: (lang: string) => Promise<any>;
}) {
  const [refLang, setRefLang] = useState<string>(translations[0]?.lang_code ?? "");
  const [overlayEntry, setOverlayEntry] = useState<{ name?: string; front?: Partial<FaceFields>; back?: Partial<FaceFields> } | null>(
    null
  );

  useEffect(() => {
    if (!refLang) return;
    let cancelled = false;
    getTranslationOverlay(refLang).then((overlayDoc) => {
      if (!cancelled) setOverlayEntry(getOverlayCardEntry(overlayDoc, card.id) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [card.id, refLang, getTranslationOverlay]);

  if (!translations.length) return null;

  const frontFace = mergeFace(card.front, overlayEntry?.front);
  const backFace = mergeFace(card.back, overlayEntry?.back);
  const displayName = overlayEntry?.name ?? card.name;

  return (
    <div className="card-editor__compare">
      <label>
        Comparer avec :
        <select value={refLang} onChange={(e) => setRefLang(e.target.value)}>
          {translations.map((t) => (
            <option key={t.id} value={t.lang_code}>
              {t.lang_code}
            </option>
          ))}
        </select>
      </label>
      <div className="card-editor__faces card-editor__faces--reference">
        <CardFaceRenderer face={frontFace} cardName={displayName} faceLabel="Recto" />
        {faceHasContent(backFace) && (
          <CardFaceRenderer
            face={backFace}
            cardName={displayName}
            faceLabel="Verso"
            showIllustration={frontFace.illustration !== backFace.illustration}
          />
        )}
      </div>
    </div>
  );
}
